import { useEffect, useId, useRef, useState } from 'react';

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
    <path
      d="M12 16V4M12 4L7 9M12 4L17 9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
    <path
      d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v6h6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB'];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

function GlassFileUpload({
  id,
  label = 'Comprobante',
  value,
  onChange,
  accept = 'image/*',
  isInvalid = false,
  disabled = false,
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const handleSelect = (event) => {
    const file = event.target.files?.[0] ?? null;
    onChange(file);
  };

  const handleClear = (event) => {
    event.preventDefault();
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      inputRef.current?.click();
    }
  };

  if (value) {
    return (
      <div className="space-y-3">
        <div className="glass-input is-valid group relative flex items-center gap-3 overflow-hidden rounded-2xl border-emerald-300/20 bg-emerald-300/[0.04] py-2 pl-2 pr-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10">
            {preview ? (
              <img
                src={preview}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <FileIcon />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white/90">
              {value.name}
            </p>
            <p className="text-[11px] font-mono uppercase tracking-wider text-white/40">
              {formatBytes(value.size)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            aria-label="Quitar comprobante"
            className="flex h-11 w-11 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white"
          >
            <XIcon />
          </button>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="text-xs font-medium text-white/50 underline-offset-4 hover:text-white/80 hover:underline"
        >
          Reemplazar imagen
        </button>
      </div>
    );
  }

  return (
    <label
      htmlFor={inputId}
      onKeyDown={handleKeyDown}
      className={`glass-input relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl px-4 py-6 text-center transition focus-within:border-violet-300/55 ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-white/20'}`}
      tabIndex={0}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        onChange={handleSelect}
        disabled={disabled}
        aria-label={label}
        className="sr-only"
      />
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06] text-white/70 ring-1 ring-white/10">
        <UploadIcon />
      </span>
      <span className="text-sm font-medium text-white/85">
        Subir comprobante
      </span>
      <span className="text-xs text-white/40">
        Arrastra o toca para seleccionar · JPG, PNG hasta 5MB
      </span>
    </label>
  );
}

export default GlassFileUpload;